import React, { useState, useEffect, useCallback } from 'react';
import { messagesAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Mail, Send, Inbox, Shield, Trash2, Plus, 
  Search, Clock, XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import moment from 'moment';

const Messages = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    receiver_id: '',
    target_role: '',
    subject: '',
    content: ''
  });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (activeTab === 'inbox') {
        response = await messagesAPI.getInbox();
      } else if (activeTab === 'sent') {
        response = await messagesAPI.getSent();
      } else if (activeTab === 'admin' && user.role === 'admin') {
        response = await messagesAPI.getAdminAll();
      }
      setMessages(response?.data || []);
    } catch (error) {
      console.error('Mesajlar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, user.role]);

  const roles = [
    { id: 'all', name: 'HERKESE MESAJ GÖNDER (Tüm Birimler)' },
    { id: 'muhasebe', name: 'Muhasebe Birimi' },
    { id: 'satis', name: 'Satış Birimi' },
    { id: 'pazarlama', name: 'Pazarlama Birimi' },
    { id: 'uretic', name: 'Üretim Birimi' },
    { id: 'nakliye', name: 'Nakliye Birimi' },
    { id: 'stok', name: 'Stok Birimi' },
    { id: 'insan_kaynaklari', name: 'İnsan Kaynakları Birimi' },
    { id: 'staff', name: 'Personel Birimi' },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchUsers();
  }, [fetchMessages, fetchUsers]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      await messagesAPI.sendMessage(formData);
      toast.success('Mesaj başarıyla gönderildi');
      setShowModal(false);
      setFormData({ receiver_id: '', target_role: '', subject: '', content: '' });
      if (activeTab === 'sent') fetchMessages();
    } catch (error) {
      console.error('Mesaj gönderilirken hata:', error);
      toast.error('Mesaj gönderilemedi');
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await messagesAPI.markAsRead(id);
      setMessages(messages.map(m => m.id === id ? { ...m, is_read: 1 } : m));
      // Bildirim sayısını güncellemek için sayfayı yenilemeden bildirim API'sini de tetikleyebiliriz
      // Ancak Layout her 30 saniyede bir zaten kontrol ediyor.
    } catch (error) {
      console.error('Okundu işaretlenirken hata:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) {
      try {
        await messagesAPI.deleteMessage(id);
        setMessages(messages.filter(m => m.id !== id));
        if (selectedMessage?.id === id) setSelectedMessage(null);
        toast.success('Mesaj silindi');
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  const filteredMessages = messages.filter(m => 
    m.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (activeTab === 'inbox' && m.sender_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (activeTab === 'sent' && m.receiver_name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="text-blue-600" />
            İç Yazışmalar / Mailler
          </h1>
          <p className="text-slate-500 text-sm mt-1">Kurum içi güvenli mesajlaşma sistemi</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Yeni Mesaj
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex border-b overflow-x-auto bg-slate-50">
          <button
            onClick={() => setActiveTab('inbox')}
            className={`px-6 py-4 flex items-center gap-2 font-medium transition-colors ${
              activeTab === 'inbox' ? 'bg-white border-b-2 border-blue-600 text-blue-600' : 'text-slate-600 hover:text-blue-600'
            }`}
          >
            <Inbox size={18} /> Gelen Kutusu
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`px-6 py-4 flex items-center gap-2 font-medium transition-colors ${
              activeTab === 'sent' ? 'bg-white border-b-2 border-blue-600 text-blue-600' : 'text-slate-600 hover:text-blue-600'
            }`}
          >
            <Send size={18} /> Gönderilenler
          </button>
          {user.role === 'admin' && (
            <button
              onClick={() => setActiveTab('admin')}
              className={`px-6 py-4 flex items-center gap-2 font-medium transition-colors ${
                activeTab === 'admin' ? 'bg-white border-b-2 border-blue-600 text-blue-600' : 'text-slate-600 hover:text-blue-600'
              }`}
            >
              <Shield size={18} /> Gizli Mod (Tüm Mailler)
            </button>
          )}
        </div>

        <div className="p-4 border-b bg-white flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Mesajlarda ara..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex h-[600px]">
          {/* Message List */}
          <div className="w-1/3 border-r overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Yükleniyor...</div>
            ) : filteredMessages.length === 0 ? (
              <div className="p-8 text-center text-slate-500 font-medium">Mesaj bulunamadı.</div>
            ) : (
              filteredMessages.map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => {
                    setSelectedMessage(msg);
                    if (activeTab === 'inbox' && !msg.is_read) handleMarkAsRead(msg.id);
                  }}
                  className={`p-4 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedMessage?.id === msg.id ? 'bg-blue-50' : ''
                  } ${!msg.is_read && activeTab === 'inbox' ? 'font-bold bg-white' : ''}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm text-slate-900 truncate pr-2">
                      {activeTab === 'inbox' ? msg.sender_name : activeTab === 'sent' ? msg.receiver_name : `${msg.sender_name} → ${msg.receiver_name}`}
                    </span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {moment(msg.created_at).fromNow()}
                    </span>
                  </div>
                  <div className="text-sm text-slate-700 truncate mb-1">{msg.subject || '(Konu Yok)'}</div>
                  <div className="text-xs text-slate-500 truncate">{msg.content}</div>
                </div>
              ))
            )}
          </div>

          {/* Message Detail */}
          <div className="w-2/3 bg-slate-50/50 flex flex-col">
            {selectedMessage ? (
              <>
                <div className="p-6 bg-white border-b shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-2">{selectedMessage.subject || '(Konu Yok)'}</h2>
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                          {(activeTab === 'inbox' ? selectedMessage.sender_name : selectedMessage.receiver_name)?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p>
                            <span className="font-semibold">Kimden:</span> {selectedMessage.sender_name} ({selectedMessage.sender_email})
                          </p>
                          <p>
                            <span className="font-semibold">Kime:</span> {selectedMessage.receiver_name} ({selectedMessage.receiver_email})
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleDelete(selectedMessage.id)}
                        className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                        title="Mesajı Sil"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-8 flex-1 overflow-y-auto bg-white m-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-6 pb-4 border-b">
                    <Clock size={14} />
                    {moment(selectedMessage.created_at).format('DD MMMM YYYY, HH:mm')}
                  </div>
                  <div className="text-slate-800 leading-relaxed whitespace-pre-wrap">
                    {selectedMessage.content}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                <Mail size={48} className="mb-4 opacity-20" />
                <p className="font-medium">Okumak için bir mesaj seçin</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b flex justify-between items-center bg-slate-50">
              <h3 className="text-xl font-bold text-slate-900">Yeni Mesaj Oluştur</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle size={24} />
              </button>
            </div>
            <form onSubmit={handleSendMessage} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">ALICI *</label>
                <select
                  required
                  className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.receiver_id || formData.target_role ? (formData.target_role ? `role:${formData.target_role}` : formData.receiver_id) : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith('role:')) {
                      setFormData({ ...formData, target_role: val.split(':')[1], receiver_id: '' });
                    } else {
                      setFormData({ ...formData, receiver_id: val, target_role: '' });
                    }
                  }}
                >
                  <option value="">Alıcı seçin...</option>
                  {user.role === 'admin' && (
                    <optgroup label="Toplu Mesaj Gönder">
                      {roles.map(r => (
                        <option key={`role-${r.id}`} value={`role:${r.id}`}>({r.name})</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Bireysel Kullanıcılar">
                    {users.filter(u => u.id !== user.id).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name ? `${u.first_name} ${u.last_name}` : u.username} ({u.role})
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Konu</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">İçerik *</label>
                <textarea
                  required
                  rows={6}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border rounded-lg hover:bg-slate-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Send size={18} />
                  Gönder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
